import type { CSSProperties } from "react";

export type OrderImageLayout = {
  /** Square tile edge in render pixels. */
  tileSize: number;
  /** Gap between tiles, both axes. */
  gap: number;
  columns: number;
  rows: number;
};

export type OrderImageStylesType = OrderImageLayout & {
  outerDiv: CSSProperties;
  gridRow: CSSProperties;
  audioButton: CSSProperties;
  audioLabel: CSSProperties;
  audioIconSize: number;
  video: CSSProperties;
  grid: CSSProperties;
  tile: CSSProperties;
  tileSelected: CSSProperties;
  tileDroppable: CSSProperties;
  tileDropTarget: CSSProperties;
  tileFrozen: CSSProperties;
  tileImage: CSSProperties;
  tileEmpty: CSSProperties;
  badge: CSSProperties;
  badgeFrozen: CSSProperties;
  badgeFontSize: number;
  hintRow: CSSProperties;
  hintText: CSSProperties;
};

export const MIN_COLUMNS = 1;
export const MAX_COLUMNS = 6;
export const DEFAULT_COLUMNS = 3;

// A rendering guard against a zero or negative tile, not a design limit: there is deliberately no
// upper bound, so a teacher who wants tiles larger than the frame gets them (the grid then clips).
export const MIN_IMAGE_SCALE = 0.1;
export const DEFAULT_IMAGE_SCALE = 1;
/** Step the web toolbar's -/+ buttons move `imageScale` by. */
export const IMAGE_SCALE_STEP = 0.1;

/** Ordering needs something to order; below two the question has no answer to give. */
export const MIN_ITEMS = 2;
/** More tiles than this leaves each one too small to tap on a phone. */
export const MAX_ITEMS = 8;

// Matches outerDiv's 3% padding on each side.
const OUTER_PADDING_RATIO = 0.06;
// Gap between tiles, and between the grid and the media/hint rows, as a fraction of tile size.
const GAP_RATIO = 0.1;
// Slack on the width budget so a row never solves to exactly 100% of the space: CSS flexbox and
// Yoga round fractional pixels differently, and without it the last tile can wrap on one platform
// only — the column count would then silently disagree between editor and phone.
const WIDTH_SAFETY_MARGIN_RATIO = 0.05;
// Floor so a full grid in a small frame never collapses to an untappable tile.
const MIN_TILE_SIZE = 24;
// Floor so the hint caption never shrinks to near-illegible on a short (content-sized) element.
const MIN_HINT_FONT_SIZE = 14;

// Chrome heights, as fractions of the frame, so the space left for the grid is identical on both
// platforms — a fixed pixel height would eat a different share of a phone frame than an editor one.
const AUDIO_ROW_RATIO = 0.14;
const VIDEO_ROW_RATIO = 0.28;
const HINT_ROW_RATIO = 0.09;
// Gap between the media row, the grid and the hint. Frame-relative rather than tile-relative so
// the height left for the grid is known before the tile size is solved for.
const SECTION_GAP_RATIO = 0.02;

// Tile trim, all relative to the tile so the whole grid scales as one.
const BORDER_RATIO = 0.02;
const RADIUS_RATIO = 0.1;
const BADGE_RATIO = 0.28;
const BADGE_FONT_RATIO = 0.13;
const BADGE_OFFSET_RATIO = 0.04;

const colors = {
  tileBorder: "#e5e7eb",
  tileBorderSelected: "#8b5cf6",
  tileBorderFrozen: "#d1d5db",
  tileBackground: "#f3f4f6",
  tileEmpty: "#e5e7eb",
  dropCandidate: "#93c5fd",
  dropCandidateFill: "#eff6ff",
  dropTarget: "#3b82f6",
  dropTargetFill: "#dbeafe",
  badge: "#8b5cf6",
  badgeFrozen: "#6b7280",
  label: "#6b7280",
  hint: "#9ca3af",
} as const;

export function clampColumns(columns: number | undefined): number {
  if (columns === undefined || Number.isNaN(columns)) return DEFAULT_COLUMNS;
  return Math.min(Math.max(Math.round(columns), MIN_COLUMNS), MAX_COLUMNS);
}

export function clampImageScale(imageScale: number | undefined): number {
  if (imageScale === undefined || Number.isNaN(imageScale)) return DEFAULT_IMAGE_SCALE;
  return Math.max(imageScale, MIN_IMAGE_SCALE);
}

export interface OrderImageLayoutOptions {
  columns?: number | undefined;
  imageScale?: number | undefined;
  mediaType?: "audio" | "video" | null | undefined;
  /** The student hint line; reserved in the editor too, or the two grids would differ in height. */
  showHint?: boolean | undefined;
}

/**
 * Pure sizing math: given the element's frame and how many tiles it holds, returns the square tile
 * size to render at. Web and mobile both call this with the same numbers — there is deliberately no
 * platform branch, since the whole point is that the editor shows what the student gets.
 *
 * Tiles auto-fit the frame at the teacher's column count (whichever of width or height binds), and
 * `imageScale` scales them from there. Above 1 the grid outgrows the frame and clips equally on
 * both platforms — rows are laid out explicitly rather than wrapped, so an oversized tile can never
 * quietly reflow the grid into a different column count than the one the teacher chose.
 */
export function calculateOrderImageLayout(
  elementWidth: number,
  elementHeight: number,
  itemCount: number,
  options: OrderImageLayoutOptions = {}
): OrderImageLayout {
  const columns = clampColumns(options.columns);
  const count = Math.max(itemCount, 1);
  const rows = Math.ceil(count / columns);

  const mediaHeight =
    options.mediaType === "video"
      ? elementHeight * VIDEO_ROW_RATIO
      : options.mediaType === "audio"
        ? elementHeight * AUDIO_ROW_RATIO
        : 0;
  const hintHeight = options.showHint ? elementHeight * HINT_ROW_RATIO : 0;
  const sectionCount = 1 + (mediaHeight > 0 ? 1 : 0) + (hintHeight > 0 ? 1 : 0);
  const sectionGaps = (sectionCount - 1) * elementHeight * SECTION_GAP_RATIO;

  const availableWidth = elementWidth * (1 - OUTER_PADDING_RATIO) * (1 - WIDTH_SAFETY_MARGIN_RATIO);
  const availableHeight =
    elementHeight * (1 - OUTER_PADDING_RATIO) - mediaHeight - hintHeight - sectionGaps;

  // n tiles + (n-1) gaps, each gap a fraction of the (unknown) tile, must fit the axis.
  const widthFit = availableWidth / (columns + (columns - 1) * GAP_RATIO);
  const heightFit = availableHeight / (rows + (rows - 1) * GAP_RATIO);
  const autoFit = Math.max(Math.min(widthFit, heightFit), MIN_TILE_SIZE);
  const tileSize = Math.max(autoFit * clampImageScale(options.imageScale), MIN_TILE_SIZE);

  return { tileSize, gap: tileSize * GAP_RATIO, columns, rows };
}

/**
 * Grid styling for the image ordering question, shared by the web editor and the mobile student
 * renderer so the teacher is authoring what the student will actually see.
 */
export function getOrderImageStyles(
  elementWidth: number,
  elementHeight: number,
  itemCount: number,
  options: OrderImageLayoutOptions = {}
): OrderImageStylesType {
  const layout = calculateOrderImageLayout(elementWidth, elementHeight, itemCount, options);
  const { tileSize, gap } = layout;

  const badgeSize = tileSize * BADGE_RATIO;
  const borderWidth = Math.max(1, tileSize * BORDER_RATIO);

  return {
    ...layout,

    outerDiv: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      padding: "3%",
      gap: elementHeight * SECTION_GAP_RATIO,
      boxSizing: "border-box",
    },

    audioButton: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: gap,
      height: elementHeight * AUDIO_ROW_RATIO,
      borderRadius: tileSize * RADIUS_RATIO,
      backgroundColor: colors.tileBackground,
      boxSizing: "border-box",
    },

    audioLabel: {
      fontSize: elementHeight * AUDIO_ROW_RATIO * 0.32,
      color: colors.label,
      fontWeight: 500,
    },

    audioIconSize: elementHeight * AUDIO_ROW_RATIO * 0.45,

    video: {
      width: "100%",
      height: elementHeight * VIDEO_ROW_RATIO,
      backgroundColor: "#000",
      borderRadius: tileSize * RADIUS_RATIO,
    },

    grid: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap,
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
    },

    gridRow: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap,
      justifyContent: "center",
    },

    tile: {
      position: "relative",
      width: tileSize,
      height: tileSize,
      flexShrink: 0,
      borderRadius: tileSize * RADIUS_RATIO,
      borderWidth,
      borderStyle: "solid",
      borderColor: colors.tileBorder,
      backgroundColor: colors.tileBackground,
      overflow: "hidden",
      boxSizing: "border-box",
    },

    tileSelected: { borderColor: colors.tileBorderSelected },

    // Editor-only drop cues. Inline rather than class-based because the tile's border width and
    // radius are computed, so anything set in CSS loses to those inline values.
    tileDroppable: {
      borderColor: colors.dropCandidate,
      borderStyle: "dashed",
      backgroundColor: colors.dropCandidateFill,
    },

    tileDropTarget: {
      borderWidth: borderWidth * 2,
      borderColor: colors.dropTarget,
      borderStyle: "dashed",
      backgroundColor: colors.dropTargetFill,
    },

    tileFrozen: { borderColor: colors.tileBorderFrozen, opacity: 0.85 },

    tileImage: { width: "100%", height: "100%", objectFit: "cover" },

    tileEmpty: { width: "100%", height: "100%", backgroundColor: colors.tileEmpty },

    badge: {
      position: "absolute",
      top: tileSize * BADGE_OFFSET_RATIO,
      right: tileSize * BADGE_OFFSET_RATIO,
      width: badgeSize,
      height: badgeSize,
      borderRadius: badgeSize / 2,
      backgroundColor: colors.badge,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    badgeFrozen: { backgroundColor: colors.badgeFrozen },

    badgeFontSize: tileSize * BADGE_FONT_RATIO,

    hintRow: {
      height: elementHeight * HINT_ROW_RATIO,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    hintText: {
      textAlign: "center",
      fontSize: Math.max(MIN_HINT_FONT_SIZE, elementHeight * HINT_ROW_RATIO * 0.5),
      color: colors.hint,
    },
  };
}

/** Items split into the teacher's declared rows, so neither platform can wrap them differently. */
export function getOrderImageRows<T>(items: T[], columns: number | undefined): T[][] {
  const perRow = clampColumns(columns);
  return Array.from({ length: Math.ceil(Math.max(items.length, 1) / perRow) }, (_, row) =>
    items.slice(row * perRow, (row + 1) * perRow)
  );
}
